import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { Input, Select, Switch, Tooltip } from 'antd';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HelpIcon } from 'src/icons/Help.icon';
import { Sort } from 'src/icons/Sort.icon';
import {
  getSRColumnMappings,
  getSRTimezone,
  setBulkSRUploadSetting,
  updateSRMappings,
} from 'src/modules/SuperAdmin/store/actions/service_request';
import { srUploadEnabledForCurrentUserSlice } from 'src/modules/SuperAdmin/store/slices/service_request.slice';
import { getOrgSetting } from 'src/store/actions/settings.action';
import { getFromStorage } from '../../../../../../lib/storage';
import organizationEventEmitter from '../../../../../../utilities/organizationEmmiter';
import { SuperAdminTable } from '../../../baseComponents/Table';

const recognizedColumnNames = [
  { name: 'Created Date Time' },
  { name: 'Start Date Time' },
  { name: 'Start Date' },
  { name: 'Start Time' },
  { name: 'Provider' },
  { name: 'Patient' },
  { name: 'Patient First Name' },
  { name: 'Patient Last Name' },
  { name: 'Phone Number' },
  { name: 'Email' },
  { name: 'Service Request Type' },
  { name: 'Status' },
  { name: 'Birth Date' },
  { name: 'Gender' },
  { name: 'Patient EHR/MRN ID' },
];

const ColumnMappingPage = () => {
  const dispatch = useDispatch();
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const {
    srColumnMappings,
    srColumnMappingsLoading,
    srTimezone,
    srTimezoneLoading,
  } = useSelector((state) => state?.serviceRequestState);
  const [selectedOrganizationUuid, setSelectedOrganizationUuid] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  const [selectedOrganizationName, setSelectedOrganizationName] =
    useState(null);
  const [bulkSRUploadEnabled, setBulkSRUploadEnabled] = useState(
    currentPractitioner?.org_settings?.enable_service_request_upload ?? false
  );
  const [organizationSettings, setOrganizationSettings] = useState({});
  const [organizationDropdown, setOrganizationDropdown] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [tableLoader, setTableLoader] = useState(false);
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );
  const [updatedValues, setUpdatedValues] = useState({});
  const [organizationName, setOrganizationName] = useState(
    currentPractitioner?.org_id
  );
  const [selectedTimezone, setSelectedTimezone] = useState('');

  const columnNameSorter = (a, b) => {
    return a.recognizedName.localeCompare(b.recognizedName);
  };

  useEffect(() => {
    setTableLoader(srColumnMappingsLoading && srTimezoneLoading);
  }, [srColumnMappingsLoading, srTimezoneLoading]);

  // tableData is the data for the Column mapping data
  // updatedValues is the data maintained for the payload to be sent while updating the mapping
  useEffect(() => {
    let dataArr = [];
    let index = 0;
    for (const column of recognizedColumnNames) {
      dataArr.push({
        key: index,
        mapping: srColumnMappings[column.name] ?? column.name,
        recognizedName: column.name,
      });
      index++;
    }
    setTableData(dataArr);

    dataArr = {};
    for (const column of recognizedColumnNames) {
      dataArr[column.name] = srColumnMappings[column.name] ?? column.name;
    }
    setUpdatedValues(dataArr);
  }, [selectedOrganizationUuid, srColumnMappings]);

  useEffect(() => {
    if (srTimezone !== null) {
      setSelectedTimezone(srTimezone);
    } else {
      setSelectedTimezone('UTC');
    }
  }, [selectedOrganizationUuid, srTimezone]);

  useEffect(() => {
    async function getOrgSettingData() {
      await dispatch(getOrgSetting(selectedOrganizationUuid)).then(
        (setting) => {
          setOrganizationSettings(setting);
        }
      );
    }
    getOrgSettingData();
    dispatch(getSRColumnMappings(selectedOrganizationUuid));
    dispatch(getSRTimezone(selectedOrganizationUuid));
  }, [selectedOrganizationUuid, dispatch]);

  const handleTimezoneChange = (e) => {
    setSelectedTimezone(e);
    let payload = {
      settings: {
        timezone: e,
      },
    };
    dispatch(updateSRMappings(payload, selectedOrganizationUuid, 'timezone'));
  };

  useEffect(() => {
    if (organizationSettings) {
      if (
        organizationSettings.enable_service_request_upload !== undefined &&
        selectedOrganizationUuid === currentPractitioner?.org_uuid
      ) {
        dispatch(srUploadEnabledForCurrentUserSlice(bulkSRUploadEnabled));
      }
    }
  }, [bulkSRUploadEnabled, organizationSettings]);

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUuid(newOrg);
        handleSelectChange(newOrg);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleSelectChange = (option) => {
    const orgUuid = option?.org_uuid;
    setSelectedOrganizationUuid(orgUuid);
    setSelectedOrganizationName(option?.label);
    setOrganizationName(option?.value);
  };

  useEffect(() => {
    async function getOrgSettingData() {
      await dispatch(getOrgSetting(selectedOrganizationUuid)).then(
        (setting) => {
          setOrganizationSettings(setting);
          setBulkSRUploadEnabled(
            setting?.enable_service_request_upload ?? false
          );
        }
      );
    }
    getOrgSettingData();
    dispatch(getSRColumnMappings(selectedOrganizationUuid));
    dispatch(getSRTimezone(selectedOrganizationUuid));
  }, [selectedOrganizationUuid, dispatch]);

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let dataArr = [];
      getActiveOrganizationsList.forEach((key, index) => {
        dataArr.push({
          value: key?.org_id,
          label: key?.org_name,
          org_uuid: key?.org_uuid,
        });
      });
      setOrganizationDropdown(dataArr);
    }
  }, [getActiveOrganizationsList]);

  const handleRadioChange = (e) => {
    setBulkSRUploadEnabled(e);
    const payload = {
      settings: {
        ...organizationSettings,
        enable_service_request_upload: e,
      },
    };
    dispatch(setBulkSRUploadSetting(selectedOrganizationUuid, payload));
    dispatch(getSRTimezone(selectedOrganizationUuid));
  };

  const handleFormSubmit = async (values) => {
    let payload = {
      settings: {
        column_mapping: updatedValues,
      },
    };
    dispatch(
      updateSRMappings(payload, selectedOrganizationUuid, 'column mapping')
    );
  };

  const handleInputChange = (e, recognizedName) => {
    setUpdatedValues({
      ...updatedValues,
      [recognizedName]: e.target.value,
    });
  };

  const columns = [
    {
      title: (
        <span className="font-[700]">
          {' '}
          Column name in Insight <Sort className={'sort-icon-column-mapping'} />
        </span>
      ),
      dataIndex: 'recognizedName',
      key: 'recognizedName',
      width: '50%',
      sorter: columnNameSorter,
      render: (text, record) => {
        return record.recognizedName === 'Appointment Notes' ? (
          <div className="flex items-center">
            <span>{text}</span>
            <Tooltip
              title={
                <div
                  style={{
                    maxWidth: '200px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  Only required for organizations with VNPT appointments
                  enabled.
                </div>
              }
              placement="right"
              overlayInnerStyle={{
                position: 'relative',
                paddingTop: '4px',
              }}
              overlayClassName="custom-tooltip"
            >
              <div className="ml-2">
                <HelpIcon />
              </div>
            </Tooltip>
          </div>
        ) : (
          text
        );
      },
    },
    {
      title: <span className="font-[700]"> Column name in CSV</span>,
      dataIndex: 'mapping',
      key: 'mapping',
      render: (text, record) => {
        return (
          <>
            <div className="flex flex-row">
              <div className="basis-1/2">
                <Input
                  value={
                    updatedValues[record.recognizedName] !== undefined
                      ? updatedValues[record.recognizedName]
                      : ''
                  }
                  onChange={(e) => {
                    handleInputChange(e, record.recognizedName);
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setUpdatedValues((prevValues) => ({
                        ...prevValues,
                        [record.recognizedName]:
                          srColumnMappings[record.recognizedName] ??
                          record.recognizedName,
                      }));
                    }
                  }}
                />
              </div>
            </div>
          </>
        );
      },
      width: '50%',
    },
  ];

  const handleReset = () => {
    let dataArr = [];
    let index = 0;
    for (const column of recognizedColumnNames) {
      dataArr.push({
        key: index,
        mapping: column.name,
        recognizedName: column.name,
      });
      index++;
    }
    setTableData(dataArr);

    dataArr = {};
    for (const column of recognizedColumnNames) {
      dataArr[column.name] = column.name;
    }
    setUpdatedValues(dataArr);
  };

  return (
    <>
      <div>
        <Formik initialValues={{}} onSubmit={handleFormSubmit}>
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <div className="flex flex-row sm:justify-between gap-4 lg:justify-between items-center">
                <div className="flex flex-row justify-start">
                  <div className="flex justify-start gap-2">
                    <div className="flex items-center">
                      <div className="mx-2">Service request bulk uploads</div>
                      <div>
                        <Switch
                          style={{
                            backgroundColor: '#00000040',
                          }}
                          value={bulkSRUploadEnabled}
                          onChange={(e) => handleRadioChange(e)}
                        />
                      </div>
                    </div>
                  </div>
                  {bulkSRUploadEnabled && (
                    <div className="flex justify-start gap-2">
                      <div
                        id="search_org"
                        className="bg-white shadow-sm rounded-xl w-60 flex items-center ml-4"
                      >
                        <Select
                          style={{
                            placeholder: {
                              fontFamily: 'Poppins',
                              color: '#667085',
                            },
                          }}
                          variant="borderless"
                          className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
                          placeholder="Select timezone"
                          options={[
                            { value: 'US/Eastern', label: 'US/Eastern' },
                            { value: 'US/Central', label: 'US/Central' },
                            { value: 'US/Mountain', label: 'US/Mountain' },
                            { value: 'US/Pacific', label: 'US/Pacific' },
                            { value: 'US/Alaska', label: 'US/Alaska' },
                            { value: 'US/Hawaii', label: 'US/Hawaii' },
                            {
                              value: 'America/Puerto_Rico',
                              label: 'US/Atlantic',
                            },
                            { value: 'Pacific/Pago_Pago', label: 'US/Samoa' },
                            { value: 'Pacific/Guam', label: 'US/Chamorro' },
                            { value: 'Asia/Kolkata', label: 'Asia/Kolkata' },
                          ]}
                          value={selectedTimezone}
                          onChange={(e) => handleTimezoneChange(e)}
                        ></Select>
                      </div>
                    </div>
                  )}
                </div>
                {bulkSRUploadEnabled && (
                  <div className="flex justify-end gap-2">
                    <div className="flex justify-end items-end">
                      <button
                        type="button"
                        onClick={handleReset}
                        className="bg-[#00D090] hover:bg-[#059669] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
                      >
                        <ReloadOutlined
                          className={`mr-2`}
                          width="14"
                          height="14"
                          viewBox="0 0 16 16"
                        />{' '}
                        Reset
                      </button>
                    </div>
                    <div className="flex justify-end items-end">
                      <button
                        type="submit"
                        className="bg-[#00D090] hover:bg-[#059669] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
                      >
                        <SaveOutlined
                          className={`mr-2`}
                          width="14"
                          height="14"
                          viewBox="0 0 16 16"
                        />{' '}
                        Save all
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {bulkSRUploadEnabled && (
                <div className="super-admin-table">
                  <div className="mt-5 ant-table rounded-xl shadow-xl">
                    <SuperAdminTable
                      className={`appointment-page-table`}
                      columns={columns}
                      data={tableData}
                      loading={tableLoader}
                    />
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default ColumnMappingPage;
